import { Controller } from "@hotwired/stimulus"

// Shared base for per-template live-preview controllers.
//
// Subclasses declare a static FIELD_BINDINGS map keyed by
// `data-live-preview-field` attribute values; the base wires `input`
// + `change` listeners on its element, looks up the changed field in
// the registry, and invokes the matching applier method with the
// normalised current value.
//
// Mounted alongside `palette-live-preview` (the horizontal cross-template
// controller that owns palette / bg-asset / filter concerns). Both
// controllers receive every event; each is a no-op for fields it does
// not own.
//
// Subclass shape:
//
//   import LivePreviewBase from "./live_preview_base"
//
//   export default class extends LivePreviewBase {
//     static targets = ["quote", "attribution"]
//
//     static FIELD_BINDINGS = {
//       quote:           { apply: "applyText" },
//       attribution:     { apply: "applyText" },
//       type_pair:       { apply: "applyTypography", events: ["change"] },
//       font_size_scale: { apply: "applyTypography" },
//     }
//
//     applyText({ value }) { ... }
//     applyTypography()    { ... }  // reads multiple fields via this.fieldValue(...)
//
//     // Optional lifecycle extensions (NOT connect/disconnect):
//     onConnect()    { ... }
//     onDisconnect() { ... }
//   }
//
// FIELD_BINDINGS keys:
//   apply  — method name on the subclass, OR a function bound to `this`.
//            Receives `{ field, event, value }`.
//   events — optional array of event types to listen to. Default: both
//            "input" and "change". Use `["change"]` to skip live-drag
//            updates from sliders, or `["input"]` for input-only.
export default class LivePreviewBase extends Controller {
  static FIELD_BINDINGS = {}

  connect() {
    this.handleEvent = this.handleEvent.bind(this)
    this.element.addEventListener("input", this.handleEvent)
    this.element.addEventListener("change", this.handleEvent)
    if (typeof this.onConnect === "function") this.onConnect()
  }

  disconnect() {
    this.element.removeEventListener("input", this.handleEvent)
    this.element.removeEventListener("change", this.handleEvent)
    if (typeof this.onDisconnect === "function") this.onDisconnect()
  }

  handleEvent(event) {
    const field = event.target?.dataset?.livePreviewField
    if (!field) return
    const binding = this.constructor.FIELD_BINDINGS[field]
    if (!binding) return
    if (binding.events && !binding.events.includes(event.type)) return
    if (event.target.type === "radio" && !event.target.checked) return

    const applier = typeof binding.apply === "function"
      ? binding.apply
      : this[binding.apply]
    if (typeof applier !== "function") return

    applier.call(this, { field, event, value: this.fieldValue(field) })
  }

  // Reads the current value of a named field, normalising across input
  // types. Returns null when the field has no inputs in scope, or when
  // a numeric input has a non-numeric value.
  //   radio    -> string value of the checked option (or null)
  //   checkbox -> boolean
  //   range    -> finite number (or null)
  //   number   -> finite number (or null)
  //   else     -> string
  fieldValue(name) {
    const els = this.element.querySelectorAll(`[data-live-preview-field="${name}"]`)
    if (!els.length) return null
    const first = els[0]
    if (first.type === "radio") {
      const checked = Array.from(els).find((el) => el.checked)
      return checked ? checked.value : null
    }
    if (first.type === "checkbox") return first.checked
    if (first.type === "range" || first.type === "number") {
      const n = parseFloat(first.value)
      return Number.isFinite(n) ? n : null
    }
    return first.value
  }

  fieldElements(name) {
    return this.element.querySelectorAll(`[data-live-preview-field="${name}"]`)
  }
}
